import React, {
  forwardRef,
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react'
import gsap from 'gsap'
import {
  EditableComponent,
  ModelProps
} from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  ScrollingBanner,
  ScrollingBannerHintVerticalPositions,
  ScrollingBannerHintHorizontalPositions,
  Video,
  ScrollingContentScreenPositions,
  useResponsiveRender
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import Container from '../Container/Container'
import EditionWrapper from '../EditionWrapper'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'

export interface VideoParallaxModelProps {
  portraitUrl: string
  landscapeUrl: string
  id?: string
  background?: string
  enableLazyLoad: boolean
}

interface ScrollingBannerModelProps {
  model?: {
    cqItems?: ScrollingBannerCqItemsModelProps
  }
  mediaRef?: object
  mobile?: boolean
  contentTopStartPosition?: ScrollingContentScreenPositions
  mediaAnimation?: gsap.core.Animation
  media?: ReactElement
  onMediaInViewChange?: (isMediaInView: boolean) => void
  onBannerScrollingAttachmentChange?: (isAttached: boolean) => void
  isBannerAttached?: boolean
  background?: string
}

interface ScrollingBannerCqItemsModelProps extends ModelProps {
  width?: string
  position?: string
}

interface ForwardRefProps {
  'data-progress': string | number
  'data-end': string | number
}

const Progress = forwardRef<HTMLDivElement, ForwardRefProps>((props, ref) => {
  const { 'data-progress': dataProgress, 'data-end': dataEnd } = props
  const config = {
    'data-progress': dataProgress,
    'data-end': dataEnd,
    ref
  }
  return <div {...config} />
})

export const VideoParallaxEditConfig = {
  emptyLabel: 'Video Parallax',
  isEmpty: true
}

const AdaptativeScrollingBanner = withComponentRefresh(
  (props: ScrollingBannerModelProps) => {
    const {
      model,
      mobile,
      mediaRef,
      contentTopStartPosition,
      mediaAnimation,
      media,
      onMediaInViewChange,
      onBannerScrollingAttachmentChange,
      isBannerAttached,
      background
    } = props
    const bannerConfig = {
      ref: mediaRef,
      contentTopStartPosition,
      mediaAnimation,
      media,
      onMediaInViewChange,
      onBannerScrollingAttachmentChange,
      background
    }
    return (
      <ScrollingBanner {...bannerConfig}>
        {model?.cqItems &&
          Object.keys(model.cqItems).map((cqItemKey, index) => {
            const {
              width,
              position,
              ':items': cqItems,
              ':itemsOrder': cqItemsOrder,
              ':type': cqType,
              ...itemRest
              // @ts-expect-error Object already checked when coming to this code
            } = model.cqItems[cqItemKey]
            let verticalPosition: ScrollingBannerHintVerticalPositions
            let horizontalPosition: ScrollingBannerHintHorizontalPositions
            switch (position) {
              case 'p2':
                verticalPosition = ScrollingBannerHintVerticalPositions.Center
                horizontalPosition =
                  ScrollingBannerHintHorizontalPositions.Start
                break
              case 'p3':
                verticalPosition = ScrollingBannerHintVerticalPositions.Center
                horizontalPosition =
                  ScrollingBannerHintHorizontalPositions.Center
                break
              case 'p4':
                verticalPosition = ScrollingBannerHintVerticalPositions.Center
                horizontalPosition = ScrollingBannerHintHorizontalPositions.End
                break
              case 'p1':
              default:
                verticalPosition = ScrollingBannerHintVerticalPositions.Start
                horizontalPosition =
                  ScrollingBannerHintHorizontalPositions.Center
                break
            }
            const finalWidth = width === 'w1' ? '80%' : '50%'
            const elementConfig = {
              model: {
                cqItems,
                cqItemsOrder,
                cqType,
                ...itemRest
              }
            }
            const hintWrapperConfig = {
              isBannerAttached,
              isFixedDisplayed: Boolean(position),
              verticalPosition: mobile
                ? ScrollingBannerHintVerticalPositions.Start
                : verticalPosition,
              horizontalPosition: mobile
                ? ScrollingBannerHintHorizontalPositions.Center
                : horizontalPosition,
              width: mobile ? '100%' : finalWidth,
              key: index
            }
            return (
              <ScrollingBanner.HintWrapper {...hintWrapperConfig}>
                <Container {...elementConfig} />
              </ScrollingBanner.HintWrapper>
            )
          })}
      </ScrollingBanner>
    )
  }
)

const VideoParallax: React.FC<
  EditableComponentPropsType<VideoParallaxModelProps>
> = withComponentRefresh((props) => {
  const { landscapeUrl, portraitUrl, id, background, enableLazyLoad, ...rest } =
    props.model
  const editableProps = useExtractEditableProps(props, rest)
  const mediaRef = useRef<HTMLVideoElement>()
  const videoProgressRef = useRef<HTMLDivElement>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [mediaAnimation, setMediaAnimation] = useState<
    gsap.core.Animation | undefined
  >()
  const [isBannerAttached, setIsBannerAttached] = useState(false)

  const onVideoLoaded = () => {
    if (mediaRef?.current) {
      setDuration(mediaRef.current.duration)
      mediaRef.current.addEventListener('play', () => {
        if (mediaRef.current && !mediaRef.current.paused) {
          mediaRef.current.pause()
        }
      })
    }
  }

  const onVideoInViewChange = (isMediaInView: boolean, videoSrc: string) => {
    if (isMediaInView && mediaRef?.current && !mediaRef.current.src) {
      mediaRef.current.src = videoSrc
    }
  }

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations?.forEach((mutation) => {
        if (mutation?.type === 'attributes') {
          setCurrentTime(
            Number.parseFloat(
              (mutation?.target as HTMLElement)?.getAttribute(
                'data-progress'
              ) || ''
            ) || 0
          )
        }
      })
    })
    if (mediaRef?.current) {
      mediaRef.current.currentTime = 0
      observer?.observe(mediaRef?.current, {
        attributes: true
      })
      const videoAnimation = gsap.timeline().fromTo(
        mediaRef?.current,
        {
          attr: { 'data-progress': 0 }
        },
        {
          attr: { 'data-progress': duration }
        }
      )
      setMediaAnimation(videoAnimation)
    }
    return () => {
      observer?.disconnect()
    }
  }, [duration])

  useEffect(() => {
    if (mediaRef?.current) {
      if (!mediaRef?.current?.paused) {
        mediaRef.current.pause()
      }
      if (
        mediaRef?.current &&
        Math.abs(currentTime - mediaRef.current.currentTime) > duration / 1000
      ) {
        mediaRef.current.currentTime = currentTime || 0
      }
    }
  }, [currentTime])

  const onMobileVideoInViewChange = (isMediaInView: boolean) => {
    onVideoInViewChange(isMediaInView, portraitUrl)
  }
  const onDesktopVideoInViewChange = (isMediaInView: boolean) => {
    onVideoInViewChange(isMediaInView, landscapeUrl)
  }
  const onBannerScrollingAttachmentChange = (isAttached: boolean) => {
    setIsBannerAttached(isAttached)
  }

  const videoConfig = {
    id,
    innerRef: mediaRef,
    autoPlay: true,
    loop: false,
    muted: true,
    controls: false,
    playsInline: true,
    lazyLoading: enableLazyLoad,
    width: '100%',
    height: '100%',
    disablePictureInPicture: true,
    onLoadEnd: onVideoLoaded,
    currentTime,
    sx: { objectFit: 'cover' }
  }
  const config = {
    mediaRef,
    mediaAnimation,
    onBannerScrollingAttachmentChange,
    isBannerAttached,
    background,
    media: (
      <>
        <Video {...videoConfig} />
        <Progress
          ref={videoProgressRef}
          data-progress={currentTime}
          data-end={duration}
        />
      </>
    ),
    model: { ...rest }
  }
  const mobileConfig = {
    ...config,
    mobile: true,
    onMediaInViewChange: onMobileVideoInViewChange,
    contentTopStartPosition: ScrollingContentScreenPositions.Middle
  }
  const desktopConfig = {
    ...config,
    onMediaInViewChange: onDesktopVideoInViewChange
  }
  const render =
    useResponsiveRender([
      <AdaptativeScrollingBanner {...mobileConfig} />,
      null,
      <AdaptativeScrollingBanner {...desktopConfig} />
    ]) || undefined

  return (
    <EditableComponent {...editableProps}>
      {AuthoringUtils.isInEditor() ? (
        <EditionWrapper label={VideoParallaxEditConfig.emptyLabel}>
          <Container {...editableProps} />
        </EditionWrapper>
      ) : (
        render
      )}
    </EditableComponent>
  )
})

const VideoParallaxWithErrorBoundary = withErrorBoundary(VideoParallax, {
  componentName: 'VideoParallax',
  fallback: <ErrorFallback componentName="VideoParallax" />
})

export default VideoParallaxWithErrorBoundary
